import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ReportShell, Stat, Table, money } from "./ReportShell";

const ymd = (d) => d.toISOString().slice(0, 10);
const yearStart = () => ymd(new Date(new Date().getFullYear(), 0, 1));

export const MarginReport = () => {
  const [from, setFrom] = useState(yearStart());
  const [to, setTo] = useState(ymd(new Date()));
  const [estimate, setEstimate] = useState(true);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/reports/margin", { params: { from, to, estimate: estimate ? 1 : 0 } });
      setData(data);
    } finally {
      setBusy(false);
    }
  };
  const t = data?.totals;
  const rows = data?.rows || [];
  const pct = (v) => (v == null ? "—" : `${v}%`);
  const tone = (v) => (v == null ? "text-zinc-400" : v < 0 ? "text-red-400" : v < 25 ? "text-amber-300" : "text-emerald-400");

  return (
    <ReportShell
      id="margin"
      title="Margin Report"
      blurb="Invoiced amount against material, labor and other costs recorded on each job. Jobs with no costs entered are flagged so the margin isn't overstated."
      range
      from={from}
      to={to}
      onFrom={setFrom}
      onTo={setTo}
      onGenerate={generate}
      busy={busy}
      generated={!!data}
      csvRows={rows}
      csvName={`margin-${from}-${to}.csv`}
    >
      <label className="mb-3 flex items-center gap-2 text-xs text-zinc-400" data-testid="margin-estimate-toggle">
        <input
          type="checkbox"
          checked={estimate}
          onChange={(e) => setEstimate(e.target.checked)}
          className="h-3.5 w-3.5 accent-yellow-500"
        />
        When no material cost is entered, estimate it from slab cost × quoted sq ft (rows marked <span className="text-sky-300">est.</span>)
      </label>
      {t && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          <Stat label="Jobs" value={t.jobs} testId="margin-stat-jobs" />
          <Stat label="Invoiced" value={money(t.invoiced)} tone="text-yellow-400" testId="margin-stat-invoiced" />
          <Stat label="Costs" value={money(t.cost_total)} tone="text-blue-400" testId="margin-stat-costs" />
          <Stat label="Margin" value={money(t.margin)} tone={tone(t.margin_pct)} testId="margin-stat-margin" />
          <Stat label="Margin %" value={pct(t.margin_pct)} tone={tone(t.margin_pct)} testId="margin-stat-pct" />
        </div>
      )}
      {t?.missing_costs > 0 && (
        <p className="mt-3 text-xs text-amber-300" data-testid="margin-missing-costs">
          {t.missing_costs} job{t.missing_costs === 1 ? "" : "s"} have no costs entered
          {t.estimated ? ` (${t.estimated} using an estimated material cost)` : ""} — click "Enter costs" on the row to add them on the job page.
        </p>
      )}
      <div className="mt-4">
        <Table
          testId="margin-table"
          rowKey={(r) => r.job_id}
          rows={rows}
          empty="No invoiced jobs in this range."
          columns={[
            {
              key: "code",
              label: "Job",
              render: (r) => (
                <Link to={`/projects/${r.job_id}?tab=field`} className="font-semibold text-yellow-400 hover:underline">
                  {r.code}
                </Link>
              ),
            },
            { key: "project_name", label: "Project", render: (r) => <span className="text-zinc-300">{r.project_name}<span className="block text-xs text-zinc-500">{r.customer_name}</span></span> },
            { key: "date", label: "Date" },
            { key: "invoiced", label: "Invoiced", right: true, render: (r) => money(r.invoiced) },
            {
              key: "cost_total",
              label: "Costs",
              right: true,
              render: (r) =>
                r.missing_costs ? (
                  <span className="flex flex-col items-end gap-0.5">
                    {r.from_lines ? (
                      <span className="text-emerald-300" title="From the Cost & Margin figures saved on the quote lines">
                        {money(r.cost_total)} <span className="text-[10px] uppercase">lines</span>
                      </span>
                    ) : r.estimated ? (
                      <span className="text-sky-300" title={`Estimated material ${money(r.cost_material)}`}>
                        {money(r.cost_total)} <span className="text-[10px] uppercase">est.</span>
                      </span>
                    ) : (
                      <span className="text-amber-300">none</span>
                    )}
                    <Link
                      to={`/projects/${r.job_id}#costs`}
                      data-testid={`margin-enter-costs-${r.job_id}`}
                      className="text-[11px] font-semibold text-yellow-400 hover:underline"
                    >
                      Enter costs →
                    </Link>
                  </span>
                ) : (
                  money(r.cost_total)
                ),
            },
            { key: "margin", label: "Margin", right: true, render: (r) => <span className={tone(r.margin_pct)}>{money(r.margin)}</span> },
            { key: "margin_pct", label: "%", right: true, render: (r) => <span className={tone(r.margin_pct)}>{pct(r.margin_pct)}</span> },
          ]}
        />
      </div>
    </ReportShell>
  );
};
